import { useEffect, useMemo, useRef, useState } from 'react';

import { AppHeader } from './components/AppHeader';
import { BoundaryNotice } from './components/BoundaryNotice';
import { StepBar } from './components/StepBar';
import {
  REQUIRED_FIELD_KEYS,
  DEFAULT_INSPECTION_SETTINGS,
  validateInspectionSettings,
  type InspectionSettings,
} from './domain';
import { CheckingScreen } from './features/inspection/CheckingScreen';
import type { ParsedWorkbookMeta } from './features/inspection/parser';
import { MappingScreen } from './features/mapping/MappingScreen';
import {
  createMappingStates,
  updateFieldMapping,
  updateHeaderRow,
  type SheetMappingState,
} from './features/mapping/model';
import { ResultsScreen } from './features/results/ResultsScreen';
import { UploadScreen } from './features/upload/UploadScreen';
import { validateSelectedFile } from './features/upload/validation';
import { RULES } from './rules';
import { InspectionWorkerClient } from './workers/client';
import type { InspectionPayload, WorkerProgressResponse } from './workers/protocol';

type Screen = 'upload' | 'mapping' | 'checking' | 'results';

const SCREEN_STEP: Readonly<Record<Screen, number>> = {
  upload: 0,
  mapping: 1,
  checking: 2,
  results: 3,
};

export default function App() {
  const workerRef = useRef<InspectionWorkerClient | null>(null);
  const operationSequenceRef = useRef(0);
  const [screen, setScreen] = useState<Screen>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [meta, setMeta] = useState<ParsedWorkbookMeta | null>(null);
  const [mappingStates, setMappingStates] = useState<readonly SheetMappingState[]>([]);
  const [settings, setSettings] = useState<InspectionSettings>(DEFAULT_INSPECTION_SETTINGS);
  const [payload, setPayload] = useState<InspectionPayload | null>(null);
  const [checkedAt, setCheckedAt] = useState<Date | null>(null);
  const [progress, setProgress] = useState<WorkerProgressResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [demoBusy, setDemoBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(
    () => () => {
      operationSequenceRef.current += 1;
      workerRef.current?.terminate();
    },
    [],
  );

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [screen]);

  const mappingValidation = useMemo(() => {
    const messages: string[] = [];
    const selected = mappingStates.filter((state) => state.selected);
    if (selected.length === 0) messages.push('请至少选择一个工作表。');
    for (const state of selected) {
      const missing = REQUIRED_FIELD_KEYS.filter(
        (field) => state.mapping.fields[field] === undefined,
      );
      if (missing.length > 0)
        messages.push(
          `${state.mapping.sheetName} 仍有 ${String(missing.length)} 个必填字段未映射。`,
        );
      const columns = Object.values(state.mapping.fields);
      if (new Set(columns).size !== columns.length)
        messages.push(`${state.mapping.sheetName} 存在重复列映射。`);
      if (!state.mapping.confirmed) messages.push(`请确认 ${state.mapping.sheetName} 的字段映射。`);
    }
    return messages;
  }, [mappingStates]);

  const handleFile = async (selectedFile: File, isDemo = false) => {
    const operationId = ++operationSequenceRef.current;
    setBusy(true);
    setDemoBusy(isDemo);
    setError(null);
    try {
      const validation = await validateSelectedFile(selectedFile);
      if (operationSequenceRef.current !== operationId) return;
      if (!validation.valid) throw new Error(validation.message);
      workerRef.current?.terminate();
      const worker = new InspectionWorkerClient();
      workerRef.current = worker;
      setProgress(null);
      const parsedMeta = await worker.parse(selectedFile, setProgress);
      if (operationSequenceRef.current !== operationId || workerRef.current !== worker) {
        worker.terminate();
        return;
      }
      setFile(selectedFile);
      setMeta(parsedMeta);
      setMappingStates(createMappingStates(parsedMeta));
      setPayload(null);
      setCheckedAt(null);
      setScreen('mapping');
    } catch (caught) {
      if (operationSequenceRef.current !== operationId) return;
      workerRef.current?.terminate();
      workerRef.current = null;
      setError(messageFrom(caught));
      setScreen('upload');
    } finally {
      if (operationSequenceRef.current === operationId) {
        setBusy(false);
        setDemoBusy(false);
      }
    }
  };

  const loadDemo = async () => {
    setBusy(true);
    setDemoBusy(true);
    setError(null);
    try {
      const response = await fetch(`${import.meta.env.BASE_URL}demo/boq-check-demo.xlsx`, {
        cache: 'no-store',
      });
      if (!response.ok) throw new Error('演示清单加载失败，请稍后重试或下载示例文件。');
      const blob = await response.blob();
      const demoFile = new File([blob], 'boq-check-demo.xlsx', {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      await handleFile(demoFile, true);
    } catch (caught) {
      setError(messageFrom(caught));
      setBusy(false);
      setDemoBusy(false);
    }
  };

  const runInspection = async (inspectionSettings: InspectionSettings = settings) => {
    if (workerRef.current === null || file === null || mappingValidation.length > 0) return;
    const settingsValidation = validateInspectionSettings(inspectionSettings);
    if (settingsValidation.length > 0) {
      setError(settingsValidation.join(' '));
      return;
    }
    const worker = workerRef.current;
    const operationId = ++operationSequenceRef.current;
    setError(null);
    setProgress(null);
    setScreen('checking');
    try {
      const mappings = mappingStates
        .filter(({ selected }) => selected)
        .map(({ mapping }) => mapping);
      const nextPayload = await worker.inspect(mappings, inspectionSettings, setProgress);
      if (operationSequenceRef.current !== operationId || workerRef.current !== worker) return;
      setSettings(inspectionSettings);
      setPayload(nextPayload);
      setCheckedAt(new Date());
      setScreen('results');
    } catch (caught) {
      if (operationSequenceRef.current !== operationId) return;
      setError(messageFrom(caught));
      setScreen(payload === null ? 'mapping' : 'results');
    }
  };

  const reset = (nextError: string | null = null) => {
    operationSequenceRef.current += 1;
    workerRef.current?.terminate();
    workerRef.current = null;
    setFile(null);
    setMeta(null);
    setMappingStates([]);
    setPayload(null);
    setCheckedAt(null);
    setProgress(null);
    setBusy(false);
    setDemoBusy(false);
    setError(nextError);
    setScreen('upload');
  };

  const updateMappingState = (index: number, state: SheetMappingState) => {
    setError(null);
    setMappingStates((current) =>
      current.map((item, itemIndex) => (itemIndex === index ? state : item)),
    );
  };

  const exportInput = () => {
    if (payload === null || checkedAt === null || file === null)
      throw new Error('当前没有可导出的检查结果。');
    return {
      sourceFileName: file.name,
      checkedAt,
      sheetNames: payload.workbook.sheets.map(({ name }) => name),
      detailRowCount: payload.result.summary.detailRowCount,
      issues: payload.result.issues,
      rules: RULES,
      settings,
      softwareVersion: '0.1.0',
    };
  };

  const exportXlsx = async () => {
    const { downloadExportedFile, exportXlsxReport } = await import('./features/export');
    const report = await exportXlsxReport(exportInput());
    downloadExportedFile(report);
  };

  const exportJson = async (): Promise<void> => {
    const { downloadExportedFile, exportJsonReport } = await import('./features/export');
    downloadExportedFile(exportJsonReport(exportInput()));
  };

  return (
    <div className="min-h-[100dvh] min-w-0 bg-slate-100 text-slate-900">
      <AppHeader compact={screen !== 'upload'} />
      {screen !== 'upload' && <StepBar current={SCREEN_STEP[screen]} />}

      {screen === 'upload' && (
        <UploadScreen
          busy={busy}
          demoBusy={demoBusy}
          error={error}
          onSelectFile={(selectedFile) => void handleFile(selectedFile)}
          onLoadDemo={() => void loadDemo()}
          onCancel={() => reset('读取已取消，文件和临时数据已从内存清除。')}
        />
      )}

      {screen === 'mapping' && meta !== null && (
        <MappingScreen
          meta={meta}
          states={mappingStates}
          error={error}
          validationMessages={mappingValidation}
          onChange={updateMappingState}
          onHeaderRowChange={(index, sheet, row) => {
            const current = mappingStates[index];
            if (current !== undefined)
              updateMappingState(index, updateHeaderRow(current, sheet, row));
          }}
          onFieldChange={(index, field, column) => {
            const current = mappingStates[index];
            if (current !== undefined)
              updateMappingState(index, updateFieldMapping(current, field, column));
          }}
          onBack={() => reset()}
          onStart={() => void runInspection()}
        />
      )}

      {screen === 'checking' && file !== null && (
        <CheckingScreen
          fileName={file.name}
          progress={progress}
          onCancel={() => reset('检查已取消，文件和临时结果已从内存清除。')}
        />
      )}

      {screen === 'results' && payload !== null && file !== null && (
        <ResultsScreen
          fileName={file.name}
          payload={payload}
          settings={settings}
          error={error}
          onReinspect={(inspectionSettings) => void runInspection(inspectionSettings)}
          onChangeFile={() => reset()}
          onExportXlsx={exportXlsx}
          onExportJson={exportJson}
        />
      )}

      <BoundaryNotice />
    </div>
  );
}

function messageFrom(value: unknown): string {
  return value instanceof Error ? value.message : '操作失败，请重试。';
}
